"use client"

import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker } from "react-day-picker"
import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/UI/button"

// Theme constants
const THEME = {
    primary: '#1E2875',    // Deep Blue
    secondary: '#4169E1',  // Royal Blue
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: `text-sm font-medium text-[${THEME.primary}]`,
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          `h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 hover:bg-[${THEME.secondary}]/10 
           border-0 transition-colors duration-200`
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell: cn(
          `text-[${THEME.primary}]/60 rounded-md w-9 font-normal text-[0.8rem]`,
          "text-center"
        ),
        row: "flex w-full mt-2",
        cell: cn(
          "h-9 w-9 text-center text-sm p-0 relative",
          "[&:has([aria-selected])]:bg-[#4169E1]/10",
          "first:[&:has([aria-selected])]:rounded-l-md",
          "last:[&:has([aria-selected])]:rounded-r-md",
          "focus-within:relative focus-within:z-20"
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal rounded-md",
          "aria-selected:opacity-100",
          `hover:bg-[${THEME.secondary}]/10 hover:text-[${THEME.primary}]`,
          `focus:bg-[${THEME.secondary}]/10 focus:text-[${THEME.primary}]`,
          "transition-colors"
        ),
        day_selected: cn(
          "bg-gradient-to-r from-[#1E2875] to-[#4169E1] text-white",
          "hover:bg-gradient-to-r hover:from-[#1E2875] hover:to-[#4169E1]",
          "hover:text-white focus:bg-[#1E2875] focus:text-white"
        ),
        day_today: `bg-[${THEME.secondary}]/10 text-[${THEME.primary}] font-semibold`,
        day_outside: `text-[${THEME.primary}]/40`,
        day_disabled: `text-[${THEME.primary}]/30`,
        day_range_middle: `aria-selected:bg-[${THEME.secondary}]/10`,
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ ...props }) => <ChevronLeft className={`h-4 w-4 text-[${THEME.primary}]`} />,
        IconRight: ({ ...props }) => <ChevronRight className={`h-4 w-4 text-[${THEME.primary}]`} />,
      }}
      {...props}
    />
  )
}

Calendar.displayName = "Calendar"

export { Calendar }